import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { citiesApi } from "./api";
import { fuzzyScoreText } from "./catalogFuzzyFilter";
import type { CityCatalog, CityCatalogOption } from "../types/api";
import type { ComboboxItem, OptionsFilter } from "@mantine/core";

export type CitySelectGroup = { group: string; items: { value: string; label: string }[] };

function normalize(raw: string): string {
  return (raw || "")
    .toLowerCase()
    .replace(/[_/|.-]+/g, " ")
    .replace(/[^a-z0-9+#.\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function displayLabel(c: CityCatalogOption): string {
  return c.state ? `${c.city_name} - ${c.state}` : c.city_name || c.label;
}

function catalogToFlatOptions(catalog: CityCatalog | undefined): { value: string; label: string }[] {
  if (!catalog?.options?.length) return [];
  return catalog.options.map((c) => ({
    value: c.label,
    label: displayLabel(c),
  }));
}

/** Kept for callers that still expect groups; pickers use flatOptions. */
function catalogToGroupedOptions(catalog: CityCatalog | undefined): CitySelectGroup[] {
  if (!catalog?.groups?.length) return [];
  return catalog.groups.map((g) => ({
    group: g.state,
    items: g.cities.map((c) => ({ value: c.label, label: displayLabel(c) })),
  }));
}

/**
 * City search ranks city-name / alias hits far above state-only matches,
 * and always returns a flat list sorted by score (best match on top).
 */
function buildCityOptionsFilter(catalog: CityCatalog | undefined): OptionsFilter {
  const byValue = new Map<string, CityCatalogOption>();
  for (const c of catalog?.options ?? []) {
    byValue.set(c.label, c);
  }

  return ({ options, search, limit }) => {
    const q = normalize(search);
    const cap =
      Number.isFinite(limit) && (limit as number) > 0 ? (limit as number) : q ? 50 : 80;

    const items = (options as ComboboxItem[]).filter(
      (o) => o && (o.value != null || o.label != null),
    );

    if (!q) {
      return items.slice(0, cap);
    }

    const scored: { item: ComboboxItem; score: number }[] = [];
    for (const item of items) {
      const meta = byValue.get(String(item.value ?? ""));
      const cityName = normalize(meta?.city_name ?? item.label?.split(" - ")[0] ?? "");
      const state = normalize(meta?.state ?? "");
      const aliases = (meta?.aliases ?? []).map(normalize).filter(Boolean);
      const label = normalize(item.label ?? "");
      const value = normalize(String(item.value ?? "").replace(/_/g, " "));

      let score = 0;
      if (cityName === q || value === q) score = 1000;
      else if (aliases.some((a) => a === q)) score = 980;
      else if (cityName.startsWith(q)) score = 960;
      else if (aliases.some((a) => a.startsWith(q))) score = 940;
      else if (cityName.includes(q)) score = 880;
      else if (aliases.some((a) => a.includes(q))) score = 860;
      else if (label.startsWith(q) || value.startsWith(q)) score = 840;
      else if (label.includes(q) || value.includes(q)) score = 780;
      else if (state && (state === q || state.startsWith(q))) score = 420;
      else if (state && state.includes(q)) score = 360;
      else {
        const compactQ = q.replace(/\s/g, "");
        const compactCity = cityName.replace(/\s/g, "");
        if (compactQ.length >= 3 && compactCity.length > 0) {
          let i = 0;
          for (let j = 0; j < compactCity.length && i < compactQ.length; j++) {
            if (compactCity[j] === compactQ[i]) i++;
          }
          if (i === compactQ.length) {
            score = 500 + Math.max(0, 60 - (compactCity.length - compactQ.length));
          }
        }
      }

      if (score > 0) scored.push({ item, score });
    }

    scored.sort(
      (a, b) =>
        b.score - a.score ||
        String(a.item.label).localeCompare(String(b.item.label)),
    );
    return scored.slice(0, cap).map((s) => s.item);
  };
}

function useCityCatalogDerived(catalog: CityCatalog | undefined) {
  const groupedOptions = useMemo(() => catalogToGroupedOptions(catalog), [catalog]);
  const flatOptions = useMemo(() => catalogToFlatOptions(catalog), [catalog]);
  const filter: OptionsFilter = useMemo(() => buildCityOptionsFilter(catalog), [catalog]);

  function resolveFromRaw(raw: string | null | undefined): string | null {
    if (!raw?.trim() || !catalog?.options?.length) return null;
    const q = raw.trim();
    const lower = q.toLowerCase();
    const exact = catalog.options.find(
      (c) =>
        c.label.toLowerCase() === lower ||
        c.city_name.toLowerCase() === lower ||
        c.code.toLowerCase() === lower ||
        (c.aliases ?? []).some((a) => a.toLowerCase() === lower),
    );
    if (exact) return exact.label;

    let best: CityCatalogOption | null = null;
    let bestScore = 0;
    for (const c of catalog.options) {
      const hay = [c.city_name, c.state, c.label, ...(c.aliases ?? [])].join(" ");
      const score = fuzzyScoreText(c.city_name, q, hay);
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
    return bestScore >= 400 && best ? best.label : null;
  }

  return { groupedOptions, flatOptions, filter, resolveFromRaw, labels: catalog?.labels };
}

/** Authenticated catalog for recruiter/HR/admin city pickers. */
export function useCityCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["city-catalog"],
    queryFn: () => citiesApi.catalog(),
    enabled,
    staleTime: 30 * 60_000,
  });
  return { ...query, ...useCityCatalogDerived(query.data) };
}

/** Public catalog for apply / signup pickers. */
export function usePublicCityCatalog(enabled = true) {
  const query = useQuery({
    queryKey: ["public-city-catalog"],
    queryFn: () => citiesApi.publicCatalog(),
    enabled,
    staleTime: 30 * 60_000,
  });
  return { ...query, ...useCityCatalogDerived(query.data) };
}
